import { LAYER_NORM_NONE } from '../../../constants/layers.ts';
import type { Lamp } from '../../../types/lamp.ts';
import type { ModelLayer } from '../../../types/model.ts';
import type { Version } from '../../../types/project.ts';
import {
  canExportPdf,
  isAutopositioningInProgress,
  isCalculationCompleted,
  isCalculationInProgress,
  isCalculationStarted,
  isJobFailed,
  isJobInProgress,
  isRenderBakingInProgress,
} from '../../../utils/jobStatus.ts';
import { isMarketMatchedVersion } from '../../../utils/lampMarketMatch';
import { DISTANCE_TO_GROUND_NA, MAX_LAMPS, type PanelSectionId } from './types';

export interface NextStepInput {
  activeSection: PanelSectionId;
  version: Version | undefined;
  lamps: Pick<Lamp, 'name' | 'distanceToGround' | 'variantId'>[];
  layers: Pick<ModelLayer, 'canHoldLuminaire' | 'isIlluminated' | 'selectedNorm'>[];
  frontendPdfPlotsEnabled: boolean;
  /** Requests sent but not yet reflected in the version's jobs. */
  pending: {
    optimization: boolean;
    autopositioning: boolean;
    cancelling: boolean;
    creatingVersion: boolean;
    marketMatch: boolean;
  };
  pdf: { ready: boolean; generating: boolean };
  marketCanStart: boolean;
  targets: { satisfied: number; total: number };
  /** Measurement-surface drawing in progress (outline or cut-out) and its point count. */
  surfaceDraw: { mode: 'surface' | 'exclusion' | null; points: number };
  /** A market-match candidate previewed on the scene (applied, not saved yet). */
  appliedMatch: { active: boolean; canCreateVersion: boolean };
  /** The project or its layers are still loading, so the version's state is not known yet. */
  loading?: boolean;
}

/** Something the user has to fix before the next step, worst first within the setup stage. */
export type SetupIssue =
  | { kind: 'mark-chl' }
  | { kind: 'mark-lmn' }
  | { kind: 'choose-norm'; count: number }
  | { kind: 'lamps-not-above'; names: string[] }
  | { kind: 'max-lamps' };

type NextStepPlan =
  /** Opening a project: nothing to say until its version (and its jobs) arrive. */
  | { stage: 'loading' }
  | { stage: 'export'; pdfReady: boolean; generating: boolean }
  | { stage: 'market-search'; running: boolean; failed: boolean; canStart: boolean; starting: boolean }
  | { stage: 'surfaces'; drawing: 'surface' | 'exclusion' | null; points: number }
  | { stage: 'applied-match'; canCreateVersion: boolean; creatingVersion: boolean }
  /** Browsing found matches in the Market section: nothing to export here, just how to proceed. */
  | { stage: 'market-results' }
  | { stage: 'optimizing'; starting: boolean }
  | { stage: 'autopositioning'; starting: boolean; cancelling: boolean }
  | { stage: 'failed'; creatingVersion: boolean }
  | {
      stage: 'completed';
      targets: { satisfied: number; total: number };
      baking: boolean;
      marketRunning: boolean;
      exportEnabled: boolean;
      /** The market search is available and not the open section. */
      offerMarket: boolean;
      hasCandidates: boolean;
      /** No market search yet: finding real catalogue luminaires becomes the main next step. */
      promoteMarket: boolean;
    }
  | { stage: 'market-matched'; unassigned: number; exportEnabled: boolean }
  | {
      stage: 'setup';
      /** Autoposition while there are no luminaires, optimization once there are. */
      action: 'autoposition' | 'optimize';
      issues: SetupIssue[];
      ready: boolean;
      canModify: boolean;
      maxReached: boolean;
      chlCount: number;
      lmnCount: number;
      lampCount: number;
    };

const hasNorm = (norm: string | null | undefined) => !!norm && norm !== LAYER_NORM_NONE;

/**
 * Where the version stands and what comes next, for the panel footer. Same rules as the legacy
 * sidebar's bottom panel. Order matters: the open section's own step (export download, market
 * search form) comes first, then running jobs, then the version's stage.
 */
export function planNextStep(input: NextStepInput): NextStepPlan {
  const { activeSection, version, lamps, layers, pending } = input;
  // Until the project arrives there is no version to judge: without this the footer would first ask
  // for CHL / LMN and offer Autoposition, then jump to e.g. "Optimizing" once the jobs load.
  if (input.loading || !version) return { stage: 'loading' };
  const exportEnabled = canExportPdf(version, { frontendPdfPlotsEnabled: input.frontendPdfPlotsEnabled });
  const marketMatched = isMarketMatchedVersion(version);
  const calcCompleted = isCalculationCompleted(version);
  const candidates = version?.lampSimilarityResult?.candidates ?? [];
  const marketRunning = isJobInProgress(version?.lampSimilarityJob);

  if (activeSection === 'export') {
    return { stage: 'export', pdfReady: input.pdf.ready, generating: input.pdf.generating };
  }

  if (activeSection === 'market' && calcCompleted && !marketMatched && candidates.length === 0) {
    return {
      stage: 'market-search',
      running: marketRunning,
      failed: isJobFailed(version?.lampSimilarityJob),
      canStart: input.marketCanStart,
      starting: pending.marketMatch,
    };
  }

  if (activeSection === 'surfaces') {
    return { stage: 'surfaces', drawing: input.surfaceDraw.mode, points: input.surfaceDraw.points };
  }

  // A previewed market match is decided first: keep it as a new version or unapply it.
  if (input.appliedMatch.active) {
    return {
      stage: 'applied-match',
      canCreateVersion: input.appliedMatch.canCreateVersion,
      creatingVersion: pending.creatingVersion,
    };
  }

  if (activeSection === 'market' && calcCompleted && !marketMatched && candidates.length > 0) {
    return { stage: 'market-results' };
  }

  if (isCalculationInProgress(version) || pending.optimization) {
    return { stage: 'optimizing', starting: pending.optimization && !isCalculationInProgress(version) };
  }

  const autopositioning = isAutopositioningInProgress(version);
  if (autopositioning || pending.cancelling || pending.autopositioning) {
    return {
      stage: 'autopositioning',
      starting: pending.autopositioning && !autopositioning,
      cancelling: pending.cancelling,
    };
  }

  if (isJobFailed(version?.calculationJob)) {
    return { stage: 'failed', creatingVersion: pending.creatingVersion };
  }

  if (calcCompleted) {
    return {
      stage: 'completed',
      targets: input.targets,
      baking: isRenderBakingInProgress(version),
      marketRunning,
      exportEnabled,
      offerMarket: !marketMatched && activeSection !== 'market',
      hasCandidates: candidates.length > 0,
      promoteMarket: !marketMatched && activeSection !== 'market' && candidates.length === 0 && !marketRunning,
    };
  }

  if (marketMatched) {
    return {
      stage: 'market-matched',
      unassigned: lamps.filter((lamp) => lamp.variantId == null).length,
      exportEnabled,
    };
  }

  const chlCount = layers.filter((l) => l.canHoldLuminaire).length;
  const lmnLayers = layers.filter((l) => l.isIlluminated);
  const missingNorms = lmnLayers.filter((l) => !hasNorm(l.selectedNorm)).length;
  const notAbove = lamps.filter((l) => l.distanceToGround === DISTANCE_TO_GROUND_NA);
  const maxReached = lamps.length >= MAX_LAMPS;
  const action = lamps.length === 0 ? 'autoposition' : 'optimize';

  const issues: SetupIssue[] = [];
  if (action === 'autoposition' && chlCount === 0) issues.push({ kind: 'mark-chl' });
  if (lmnLayers.length === 0) issues.push({ kind: 'mark-lmn' });
  if (missingNorms > 0) issues.push({ kind: 'choose-norm', count: missingNorms });
  if (notAbove.length > 0) issues.push({ kind: 'lamps-not-above', names: notAbove.map((l) => l.name) });
  if (maxReached) issues.push({ kind: 'max-lamps' });

  return {
    stage: 'setup',
    action,
    issues,
    ready: issues.length === 0,
    canModify: !isCalculationStarted(version),
    maxReached,
    chlCount,
    lmnCount: lmnLayers.length,
    lampCount: lamps.length,
  };
}
